import { useCallback, useEffect, useMemo, useReducer, type ReactNode } from 'react';
import { apiCall, ApiError, installRefreshHook } from './api';
import {
  AuthUserSchema,
  LoginResponseSchema,
  RefreshResponseSchema,
  type AuthUser,
  type LoginResponse,
} from './schemas/user';
import { AuthContext, type AuthContextValue, type AuthState, type AuthStatus } from './auth-context';

type Action =
  | { type: 'BOOT_DONE'; payload: { user: AuthUser; accessToken: string } | null }
  | { type: 'LOGIN'; payload: LoginResponse }
  | { type: 'REFRESH'; payload: { accessToken: string } }
  | { type: 'PASSWORD_CHANGED' }
  | { type: 'LOGOUT' };

const initialState: AuthState = { status: 'loading', accessToken: null, user: null };

function deriveStatus(user: AuthUser | null): AuthStatus {
  if (!user) return 'guest';
  return user.mustChangePassword ? 'must_change' : 'authed';
}

function reducer(state: AuthState, action: Action): AuthState {
  switch (action.type) {
    case 'BOOT_DONE':
      if (!action.payload) return { status: 'guest', accessToken: null, user: null };
      return {
        status: deriveStatus(action.payload.user),
        accessToken: action.payload.accessToken,
        user: action.payload.user,
      };
    case 'LOGIN':
      return {
        status: deriveStatus(action.payload.user),
        accessToken: action.payload.accessToken,
        user: action.payload.user,
      };
    case 'REFRESH':
      return { ...state, accessToken: action.payload.accessToken };
    case 'PASSWORD_CHANGED':
      // Backend clears the refresh cookie + revokes refresh tokens, so we drop
      // back to guest and force a fresh login with the new password.
      return { status: 'guest', accessToken: null, user: null };
    case 'LOGOUT':
      return { status: 'guest', accessToken: null, user: null };
    default:
      return state;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const refreshAccess = useCallback(async (): Promise<string | null> => {
    try {
      const res = await apiCall<unknown>('/auth/refresh', { method: 'POST', body: {} });
      const parsed = RefreshResponseSchema.safeParse(res.data);
      if (!parsed.success) return null;
      dispatch({ type: 'REFRESH', payload: { accessToken: parsed.data.accessToken } });
      return parsed.data.accessToken;
    } catch {
      return null;
    }
  }, []);

  // Make refreshAccess available to `apiCall` so any 401 AUTH_REQUIRED on an
  // authenticated request can transparently retry once with a fresh token.
  useEffect(() => {
    installRefreshHook(refreshAccess);
    return () => installRefreshHook(null);
  }, [refreshAccess]);

  // Boot: try to mint an access token from the refresh cookie, then GET /auth/me.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const accessToken = await refreshAccess();
      if (cancelled) return;
      if (!accessToken) {
        dispatch({ type: 'BOOT_DONE', payload: null });
        return;
      }
      try {
        const me = await apiCall<unknown>('/auth/me', { accessToken });
        const parsed = AuthUserSchema.safeParse(me.data);
        if (!parsed.success || cancelled) {
          dispatch({ type: 'BOOT_DONE', payload: null });
          return;
        }
        dispatch({ type: 'BOOT_DONE', payload: { user: parsed.data, accessToken } });
      } catch {
        if (!cancelled) dispatch({ type: 'BOOT_DONE', payload: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshAccess]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiCall<unknown>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    const parsed = LoginResponseSchema.parse(res.data);
    dispatch({ type: 'LOGIN', payload: parsed });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiCall<unknown>('/auth/logout', {
        method: 'POST',
        body: {},
        accessToken: state.accessToken,
      });
    } catch (err) {
      // 401 on logout is expected if the access token already expired —
      // the cookie is cleared server-side regardless.
      if (!(err instanceof ApiError) || err.status !== 401) throw err;
    } finally {
      try {
        localStorage.removeItem('cosmobud_hide_search_banner');
      } catch {
        // ignore
      }
      dispatch({ type: 'LOGOUT' });
    }
  }, [state.accessToken]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await apiCall<unknown>('/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
        accessToken: state.accessToken,
      });
      dispatch({ type: 'PASSWORD_CHANGED' });
    },
    [state.accessToken],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, changePassword, refreshAccess }),
    [state, login, logout, changePassword, refreshAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
